/**
 * One row of the NavLog: the departure, a leg, the totals or the alternate.
 *
 * A row that cannot show all its columns carries an expander, and what it
 * reveals is the same cells in a definition list — not a second, read-only copy
 * of them. There is one editable surface per value in this app, and a narrow
 * pane must not be the reason a pilot cannot change a wind.
 */
import { useState } from 'react';

import { useT } from '../../i18n';
import { formatFuel, legFuelL, feetToSafeAlt, safeAltToFeet, type NavRow } from './model';
import { cellFor, type CellSpec, type Column } from './columns';
import { BLANK, CellComputed, CellInput, CellValue, DASH, type Transform } from './NavLogCell';

/**
 * The expander's chevron. Inline, because the two glyphs a chevron is usually
 * made of (⌄ ›) are not in Plex and would fall back to a system face — a 6 px
 * mismatch nobody can name but everybody sees.
 */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={'nl-chev' + (open ? ' open' : '')}
      width="8"
      height="8"
      viewBox="0 0 8 8"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 1.5 L5.5 4 L2 6.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

/** The sheet keeps thousands of feet; the pilot reads and types feet (§9.2). */
const FEET: Transform = { toInput: safeAltToFeet, fromInput: feetToSafeAlt };

export interface RowProps {
  row: NavRow;
  columns: readonly Column[];
  /** the columns this width dropped — the expander's contents */
  hidden: readonly Column[];
  /** litres per hour, or null when the plan has no consumption */
  lph: number | null;
  /** this leg's midpoint or altitude moved since the wind was read */
  windStale: boolean;
  /** a shared plan: nothing here is typed into, so nothing empty is offered */
  readOnly: boolean;
  read(id: string): string;
}

/**
 * The word that stands in for the „Von" half.
 *
 * Only the departure needs one: it is the one row whose checkpoint is not the
 * end of anything. The alternate says what it is in the leg column (`A`) and the
 * totals row has its own cell, so a second word there would be a second label
 * for one fact.
 */
function fromLabel(row: NavRow, t: (k: string) => string): string | null {
  return row.kind === 'dep' ? t('form.dep') : null;
}

function Cell({
  col, spec, row, lph, windStale, read,
}: {
  col: Column;
  spec: CellSpec;
  row: NavRow;
  lph: number | null;
  windStale: boolean;
  read(id: string): string;
}) {
  const t = useT();

  if (spec.kind === 'none') return <span className="nl-val">{BLANK}</span>;

  if (spec.kind === 'label') return <span className="nl-rowlabel">{t('form.total')}</span>;

  if (spec.kind === 'fuel') {
    const l = legFuelL(lph, read(spec.time));
    return <CellComputed value={formatFuel(l)} title={t('th.fuel') + ' – ' + t('navlog.computed')} />;
  }

  if (spec.kind === 'pair') {
    const word = fromLabel(row, t);
    return (
      <span className="nl-pair">
        {word !== null ? <span className="nl-rowlabel">{word}</span> : null}
        {spec.from ? <CellValue id={spec.from} className="nl-from" /> : null}
        {spec.from ? <span className="nl-arrow" aria-hidden="true">{'→'}</span> : null}
        <CellInput id={spec.to} />
      </span>
    );
  }

  const computedColumn = col.editable !== true;
  if (!spec.editable) {
    return <CellValue id={spec.id} {...(computedColumn ? { computedColumn: true } : {})} />;
  }

  const isAltWind = col.key === 'wind' && row.kind === 'alt';
  return (
    <span className="nl-cellwrap">
      <CellInput
        id={spec.id}
        {...(col.key === 'safealt' ? { transform: FEET } : {})}
        {...(isAltWind ? { placeholder: DASH, note: t('navlog.alt.wind') } : {})}
      />
      {windStale && col.key === 'wind' ? (
        <i className="nl-stale" title={t('winds.stale')} aria-label={t('winds.stale')} role="img" />
      ) : null}
    </span>
  );
}

export function NavLogRow({ row, columns, hidden, lph, windStale, readOnly, read }: RowProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  // a row expands when it actually has something hidden — the departure has one
  // cell (its VOR/NDB) and the totals row has none. A reader gets the chevron
  // only where there is a value behind it.
  const canExpand = row.kind !== 'total' && hidden.some(c => {
    const spec = cellFor(c, row);
    if (spec.kind === 'none') return false;
    if (!readOnly) return true;
    return spec.kind === 'fuel' ? read(spec.time) !== '' : spec.kind === 'field' && read(spec.id) !== '';
  });
  const label = row.kind === 'leg' ? String(row.row) : row.kind === 'alt' ? t('navlog.row.alt') : '';
  const rowTitle =
    row.kind === 'leg' ? t('form.leg', { n: String(row.row) })
      : row.kind === 'alt' ? t('form.alt')
        : row.kind === 'dep' ? t('form.dep')
          : t('form.total');

  return (
    <>
      <tr className={'nl-r nl-r-' + row.kind + (open ? ' open' : '')}>
        {columns.map(col =>
          col.key === 'idx' ? (
            <td key={col.key} className="nl-c nl-c-idx">
              {canExpand ? (
                <button
                  type="button"
                  className="nl-expand"
                  aria-expanded={open}
                  aria-label={rowTitle}
                  title={rowTitle}
                  onClick={() => setOpen(v => !v)}
                >
                  <span className="ae-num">{label || BLANK}</span>
                  <Chevron open={open} />
                </button>
              ) : (
                <span className="ae-num nl-idx" title={rowTitle}>{label || BLANK}</span>
              )}
            </td>
          ) : (
            <td
              key={col.key}
              className={'nl-c nl-c-' + col.key + (col.text ? ' text' : ' num')}
            >
              <Cell
                col={col}
                spec={cellFor(col, row)}
                row={row}
                lph={lph}
                windStale={windStale}
                read={read}
              />
            </td>
          ),
        )}
      </tr>
      {open ? (
        <tr className="nl-r nl-detail">
          <td colSpan={columns.length}>
            {/* sticky, so an expanded row stays readable in a table scrolled
                sideways — the detail is about the row, not about the columns */}
            <dl className="nl-dl">
              {hidden.map(col => {
                const spec = cellFor(col, row);
                if (spec.kind === 'none') return null;
                return (
                  <div className="nl-dd" key={col.key}>
                    <dt className="ae-eyebrow" {...(col.full ? { title: t(col.full) } : {})}>
                      {t(col.head)}
                      {col.unit ? <span className="nl-unit">{col.unit}</span> : null}
                    </dt>
                    <dd className={col.text ? 'text' : 'num'}>
                      <Cell
                        col={col}
                        spec={spec}
                        row={row}
                        lph={lph}
                        windStale={windStale}
                        read={read}
                      />
                    </dd>
                  </div>
                );
              })}
            </dl>
          </td>
        </tr>
      ) : null}
    </>
  );
}
